import { PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Empty, Form, Typography, notification, theme } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'libs/dayjs';
import { useObservacionAcciones } from 'providers/ObservacionAccionesProvider';
import { useObservacionesTypes } from 'providers/ObservacionesTypesProvider';
import React, { useEffect, useMemo, useState } from 'react';

// Las entradas de esta sección son SIEMPRE de estilo bitácora: el tipo se fija
// al ObservationType "Bitácora" y el selector de tipo queda oculto. Resolvemos
// el id por nombre (en vez de hardcodearlo) para que no dependa del autoincrement
// que quede tras aplicar la migración en cada entorno.
const BITACORA_TYPE_NAME = 'Bitácora';

const BitacoraEdificio = ({ idCliente, observaciones, onAdded }) => {
  const [form] = Form.useForm();
  const [composerOpen, setComposerOpen] = useState(false);
  const { token } = theme.useToken();

  const { call: callTypes, hasCalled: hasCalledTypes, data: types } = useObservacionesTypes();
  const { create, isLoadingCreate } = useObservacionAcciones();

  useEffect(() => {
    if (!hasCalledTypes) callTypes();
  }, [callTypes, hasCalledTypes]);

  const bitacoraType = useMemo(
    () => (types || []).find((t) => t?.name?.toLowerCase() === BITACORA_TYPE_NAME.toLowerCase()),
    [types]
  );

  // Orden cronológico inverso: la entrada más reciente arriba.
  const ordered = useMemo(
    () => [...(observaciones || [])].sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt))),
    [observaciones]
  );

  const handleSubmit = async ({ description }) => {
    if (!bitacoraType) return;
    try {
      await create({
        id_cliente: idCliente,
        observation_type_id: bitacoraType.id,
        description,
      });
      form.resetFields();
      setComposerOpen(false);
      onAdded?.();
    } catch (error) {
      notification.error({ message: 'Error', description: error.message });
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: token.marginMD }}>
        <Typography.Title level={5} style={{ margin: 0, color: token.colorText }}>
          Bitácora del edificio
        </Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {ordered.length} {ordered.length === 1 ? 'entrada' : 'entradas'}
        </Typography.Text>
      </div>

      {/* Inline composer */}
      {!composerOpen ? (
        <Button
          icon={<PlusOutlined />}
          onClick={() => setComposerOpen(true)}
          style={{
            width: '100%',
            marginBottom: token.marginMD,
            borderStyle: 'dashed',
            color: token.colorTextSecondary,
            borderColor: token.colorBorder,
          }}
        >
          Agregar entrada a bitácora
        </Button>
      ) : (
        <div
          style={{
            border: `1px solid ${token.colorBorder}`,
            borderRadius: token.borderRadius,
            padding: token.paddingSM,
            marginBottom: token.marginMD,
            background: token.colorBgContainer,
          }}
        >
          {hasCalledTypes && !bitacoraType && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: token.marginSM }}
              message="No se encontró el tipo 'Bitácora'"
              description="Aplicá la migración 71.0.sql para habilitar las entradas de bitácora."
            />
          )}
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="description"
              style={{ marginBottom: token.marginSM }}
              rules={[{ required: true, message: 'Escribí la entrada de bitácora' }]}
            >
              <TextArea
                rows={3}
                placeholder="Escribe aquí la entrada de bitácora del edificio"
                autoFocus
              />
            </Form.Item>
            <div style={{ display: 'flex', gap: token.marginXS, justifyContent: 'flex-end' }}>
              <Button
                onClick={() => {
                  form.resetFields();
                  setComposerOpen(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoadingCreate}
                disabled={!bitacoraType}
              >
                Guardar entrada
              </Button>
            </div>
          </Form>
        </div>
      )}

      {/* Timeline list */}
      {ordered.length === 0 ? (
        <Empty
          style={{ margin: `${token.marginLG}px 0` }}
          description="Sin entradas de bitácora"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <div style={{ maxHeight: 340, overflowY: 'auto', paddingRight: 4 }}>
          {ordered.map((obs, index) => (
            <div
              key={obs.id}
              style={{
                display: 'flex',
                gap: token.marginSM,
                paddingBottom: token.paddingSM,
                marginBottom: token.marginXS,
                position: 'relative',
              }}
            >
              {/* Vertical line */}
              {index < ordered.length - 1 && (
                <div
                  style={{
                    position: 'absolute',
                    left: 7,
                    top: 16,
                    bottom: 0,
                    width: 2,
                    background: token.colorBorderSecondary,
                  }}
                />
              )}
              {/* Dot */}
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: token.colorPrimary,
                  flexShrink: 0,
                  marginTop: 2,
                  zIndex: 1,
                }}
              />
              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Typography.Text
                  className="mono"
                  type="secondary"
                  style={{ fontSize: token.fontSizeSM, display: 'block', marginBottom: 2 }}
                >
                  {dayjs(obs.createdAt).format('DD/MM/YY · HH:mm')}
                </Typography.Text>
                <div
                  style={{
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadius,
                    padding: `${token.paddingXS}px ${token.paddingSM}px`,
                    background: token.colorBgContainer,
                  }}
                >
                  <Typography.Text style={{ fontSize: token.fontSize }}>
                    {obs.description}
                  </Typography.Text>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BitacoraEdificio;
